/** @jsx jsx */
import React, { Fragment } from 'react'
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import _ from 'lodash'
import useRootStore from '@store/useRootStore'
import { HOME_OR_AWAY_SCORE } from '@constants/index'
import dayjs from 'dayjs'
import { GAME_TYPE } from '@game-type/game-market-types'
import { IframeWrap, LiveScoreIframeWrap, TxtLimit } from '@styles/base.style'
import HomeTagImg from '@assets/images/icon/matchup_icon/home-tag-ico.png'
import Iframe from 'react-iframe'
import { isMobile } from 'react-device-detect'
import {
  convertRate,
  getHomeOrAwayLiveScoreWithPeriodScores,
  isLsportsEnv,
  isSetScoreSport,
  isShowMatchRecordButton,
} from '@utils/utils'
import { EventStatusType } from '@game-type/betradar-type'
import { LS_SPORT_ID, MATCH_GAME_STATUS, SPORT_ID } from 'adv-protocol/src/GameCommon'
import { SimpleMatchTrack } from '@store/matchupStore'
import { GamePreviewWrap, MatchTeamInfoWrap, MatchWrapper, normalBet, straightBet } from '@styles/match.style'
import { LS_SPORT_ID_ICON_MAP, SPORT_ID_ICON_MAP } from '@shared/SportIdGroup'
import LeagueEmblemImg from '@assets/images/icon/emblem_icon/league.png'
import { BASIC_TEAM_EMBLEM_MAP } from '@shared/EmblemGroup'
import { LS_SPORTS_TYPE_NUM, PAGE_TYPE, SPORTS_TYPE_NUM } from '@interface/common'
import { MatchChart } from '@views/Home/index.style'
import LiveSoccerBg from '@assets/images/bg/match/soccer.jpg'
import LiveBasketballBg from '@assets/images/bg/match/basketball.jpg'
import LiveBaseballBg from '@assets/images/bg/match/baseball.jpg'
import LiveIceHokeyBg from '@assets/images/bg/match/ice_hockey.jpg'
import LiveVolleyballBg from '@assets/images/bg/match/volleyball.jpg'
import LiveTableTennisBg from '@assets/images/bg/match/table_tennis.jpg'
import LiveDefaultBg from '@assets/images/bg/match/common_sports.jpg'
import { ArrDayText } from '@game/util'
import { makeVolleyballScore } from '@utils/jsxutils'
import classNames from 'classnames'
import { MatchSimpleInfoWrap, VisualNotice } from '@views/Matchup/MatchUpDetail/index.style'
import { ToolTipIco } from '@shared/Icon/MatchupIcon'
import { Schema$Outcome } from '@protocol/game'
import { Schema$UserPickWayRatio } from '@interface/user-pick'
import { MatchRecordButton } from '@components/Button/MatchRecordButton'
import DialogUtils from '@utils/dialog-utils'

export interface SimpleMatchInfoProps {
  track: SimpleMatchTrack
  outcomes: Schema$Outcome[]
  wayRatio: Schema$UserPickWayRatio
  visualSupport: boolean
  gameKey: GAME_TYPE
  pageType: PAGE_TYPE
  liveScoreIframeWrapClassName: string
  simpleMatchInfoClassName: string
  iframeWrapTypeClassName: string
}

export const SimpleMatchInfo: React.FC<SimpleMatchInfoProps> = observer(props => {
  const { globalStore, authStore } = useRootStore()

  const {
    track,
    outcomes,
    wayRatio,
    visualSupport,
    gameKey,
    pageType,
    liveScoreIframeWrapClassName,
    simpleMatchInfoClassName,
    iframeWrapTypeClassName,
  } = props

  // track 데이터가 없으면 렌더하지 않음
  if (!track) return null

  const {
    matchId,
    dateOfMatch = new Date(),
    sportId,
    categoryName,
    tournamentName,
    tournamentEmblem,
    homeTeamName = '홈팀',
    awayTeamName = '원정팀',
    homeTeamEmblem,
    awayTeamEmblem,
    feedSupplier,
  } = track

  // ─── 페이지 / 경기 타입 플래그 ─────────────────────────────────────────────
  /** 결과 페이지 (경기결과 목록, 경기결과 필터) */
  const isMatchupResult = pageType === PAGE_TYPE.MATCHUP_RESULT || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER
  /**
   * 라이브 진행 중 경기:
   *   - 라이브 탭에서 열린 경우 (gameKey === LIVE)
   *   - 결과 페이지에서 아직 진행 중인 경기 (statusCode === Live)
   */
  const isLiveMatch = gameKey === GAME_TYPE.LIVE || track.statusCode === MATCH_GAME_STATUS.Live
  /** 비주얼라이제이션 iframe 활성화 조건 */
  const isInProgress = track.statusCode === EventStatusType.Live
  const isVisualSupportOn = gameKey === GAME_TYPE.LIVE || (isMatchupResult && isInProgress)

  // ─── 스포츠 종목 플래그 ────────────────────────────────────────────────────
  const isBaseball = sportId === SPORT_ID.BASEBALL || sportId === LS_SPORT_ID.BASEBALL
  const isTableTennis = visualSupport && sportId === SPORT_ID.TABLE_TENNIS
  /** 세트 스코어 표시 종목 (배구, LS배구, 탁구, 테니스) */
  const isSetScore = isSetScoreSport(sportId)

  // ─── 클래스명 계산 ─────────────────────────────────────────────────────────
  const iframeWrapClassName = classNames('detail-bet-iframe', {
    'live-extended': isLiveMatch,
    'pc': !isMobile,
  })

  const homeTagClass = classNames('home-tag-img', {
    'pc': !isMobile,
    'table-tennis': isTableTennis,
  })

  // 탁구(비주얼라이제이션 지원 종목)와 야구는 홈팀 태그 모양이 다름
  const homeTagBaseballClass = classNames({
    'baseball': isBaseball,
    'table-tennis': isTableTennis && gameKey === GAME_TYPE.LIVE,
  })

  const detailBetClassName = classNames('detail-bet', {
    'result-page': isMatchupResult,
  })

  // ─── 비주얼라이제이션 URL ───────────────────────────────────────────────────
  // matchup.scoreboard 라이센스 관련(벳레이더) 작업이 시간이 소요됨으로
  // 기존 비주얼라이제이션에서 상세스코어를 라이브일 경우에만 보이도록 적용
  // matchup.scoreboard 라이센스 해결 된 후에 다시 작업 필요
  let convertedUrl = globalStore.getVisualizationUrl(matchId, sportId, gameKey, feedSupplier, track)
  if (isVisualSupportOn) {
    convertedUrl = convertedUrl.replace('&momentum=disable', '').concat('&detailedScoreboard=scoreDetails')
  }

  // ─── 라이브 배경 이미지 ─────────────────────────────────────────────────────
  let matchGroupBgImage
  switch (sportId) {
    case LS_SPORTS_TYPE_NUM.SOCCER:
    case SPORTS_TYPE_NUM.SOCCER:
      matchGroupBgImage = LiveSoccerBg
      break
    case LS_SPORTS_TYPE_NUM.BASKETBALL:
    case SPORTS_TYPE_NUM.BASKETBALL:
      matchGroupBgImage = LiveBasketballBg
      break
    case LS_SPORTS_TYPE_NUM.BASEBALL:
    case SPORTS_TYPE_NUM.BASEBALL:
      matchGroupBgImage = LiveBaseballBg
      break
    case LS_SPORTS_TYPE_NUM.ICE_HOCKEY:
    case SPORTS_TYPE_NUM.ICE_HOCKEY:
      matchGroupBgImage = LiveIceHokeyBg
      break
    case LS_SPORTS_TYPE_NUM.VOLLEYBALL:
    case SPORTS_TYPE_NUM.VOLLEYBALL:
      matchGroupBgImage = LiveVolleyballBg
      break
    case SPORTS_TYPE_NUM.TABLE_TENNIS:
      matchGroupBgImage = LiveTableTennisBg
      break
    default:
      matchGroupBgImage = LiveDefaultBg
      break
  }

  // ─── 날짜 / 시간 텍스트 ─────────────────────────────────────────────────────
  const dateInst = new Date(dateOfMatch)
  const dateText = dayjs(dateOfMatch).format('MM/DD')
  const monthText = ArrDayText[new Date(dateInst).getDay()]
  const dateOfMatchText = `${dateText}(${monthText})`
  const timeText = dayjs(dateOfMatch).format('HH:mm')

  // ─── 스코어 계산 ────────────────────────────────────────────────────────────
  // NOTE: 배구/탁구/테니스는 합산 점수가 아닌 세트별 점수를 표시하는 방식
  // getHomeOrAwayLiveScoreWithPeriodScores가 "현재 세트 점수 합산 스코어" 형태의 문자열을 반환하고
  // makeVolleyballScore로 "현재 세트 점수"와 "세트 스코어 배열"을 분리함
  const { awayScore, homeScore, matchStatusName } = track
  const matchStatusLabel = matchStatusName ?? ''
  const hScore = homeScore
  const aScore = awayScore

  let live_hScore = getHomeOrAwayLiveScoreWithPeriodScores(HOME_OR_AWAY_SCORE.HOME, track, sportId, isLiveMatch)
  let live_aScore = getHomeOrAwayLiveScoreWithPeriodScores(HOME_OR_AWAY_SCORE.AWAY, track, sportId, isLiveMatch)
  let eleHomeScore = []
  let eleAwayScore = []

  if (isSetScore) {
    const volleyballHomeScore = makeVolleyballScore(live_hScore)
    const volleyballAwayScore = makeVolleyballScore(live_aScore)
    live_hScore = volleyballHomeScore.periodScore
    eleHomeScore = volleyballHomeScore.setScore
    live_aScore = volleyballAwayScore.periodScore
    eleAwayScore = volleyballAwayScore.setScore
  }

  // ─── 승/패 스타일 계산 ─────────────────────────────────────────────────────
  // 스크롤 시 고정되는 MatchSimpleInfoWrap 영역의 팀명 하이라이트용
  let styleHomeScore: number | string = _.toNumber(isLiveMatch ? live_hScore : hScore)
  let styleAwayScore: number | string = _.toNumber(isLiveMatch ? live_aScore : aScore)

  let homeClassName = ''
  let awayClassName = ''
  let homeScoreNumClassName = ''
  let awayScoreNumClassName = ''

  if (styleHomeScore > styleAwayScore) {
    homeClassName = 'win'
    awayClassName = 'lose'
    homeScoreNumClassName = 'win'
  } else if (styleHomeScore < styleAwayScore) {
    homeClassName = 'lose'
    awayClassName = 'win'
    awayScoreNumClassName = 'win'
  }

  // 세트 스코어 종목에서 세트 수가 아직 없는 경우 '-'로 표시
  if (_.isNaN(styleHomeScore) || _.isNaN(styleAwayScore)) {
    styleHomeScore = '-'
    styleAwayScore = '-'
  }

  // ─── 배당 / 픽 비율 계산 ────────────────────────────────────────────────────
  let homeRate
  let drawRate
  let awayRate
  let isTwoWay = false

  if (!_.isNil(outcomes)) {
    if (outcomes.length === 2) {
      isTwoWay = true
      homeRate = outcomes[0].rate
      awayRate = outcomes[1].rate
    } else if (outcomes.length === 3) {
      homeRate = outcomes[0].rate
      drawRate = outcomes[1].rate
      awayRate = outcomes[2].rate
    }
  }

  const isDrawScore = !isTwoWay
  const isHomeWin = outcomes?.[0]?.result ?? false
  const isAwayWin = isTwoWay ? (outcomes?.[1]?.result ?? false) : (outcomes?.[2]?.result ?? false)
  const isDrawWin = isTwoWay ? false : (outcomes?.[1]?.result ?? false)

  const oddsClass = classNames('odd-rate result-page', {
    'is-draw-score': isDrawScore,
    'none': _.isNil(homeRate) && _.isNil(drawRate) && _.isNil(awayRate),
    'adv-hidden': gameKey === GAME_TYPE.LIVE,
  })

  const winRatio = wayRatio?.home
  const drawRatio = wayRatio?.draw
  const loseRatio = wayRatio?.away

  // ─── 내부 렌더 함수 ─────────────────────────────────────────────────────────

  /** 배당 표시 — 결과 페이지 / 프리매치 공통으로 사용 */
  function renderDividendBoard() {
    return (
      <div className="dividend-board">
        <span className={oddsClass} css={isHomeWin ? straightBet : normalBet}>
          {convertRate(authStore.isSignedIn, homeRate)}
        </span>
        {isDrawScore && (
          <span className={oddsClass} css={isDrawWin ? straightBet : normalBet}>
            {convertRate(authStore.isSignedIn, drawRate)}
          </span>
        )}
        <span className={oddsClass} css={isAwayWin ? straightBet : normalBet}>
          {convertRate(authStore.isSignedIn, awayRate)}
        </span>
      </div>
    )
  }

  /**
   * Case 1 — 라이브 진행 중 (isLiveMatch = true)
   * : 실시간 스코어 + 경기 상태 텍스트
   *
   * 예외: 결과 페이지에서 gameKey=LIVE이지만 경기가 이미 종료된 경우
   * → statusCode !== Live이므로 실시간 스코어 대신 최종 스코어(hScore/aScore)를 표시
   */
  function renderLiveGameInfo() {
    const isClosedOnResultPage = isMatchupResult && track.statusCode !== MATCH_GAME_STATUS.Live
    const displayHScore = isClosedOnResultPage ? hScore : live_hScore
    const displayAScore = isClosedOnResultPage ? aScore : live_aScore

    return (
      <Fragment>
        <div className="score live">
          <span className={`home-score ${homeClassName}`}>
            {isSetScore && <div className="volleyball-score home">{eleHomeScore}</div>}
            {displayHScore}
          </span>
          <span className="center">{isClosedOnResultPage ? '' : ':'}</span>
          <span className={`away-score ${awayClassName}`}>
            {isSetScore && <div className="volleyball-score away">{eleAwayScore}</div>}
            {displayAScore}
          </span>
        </div>
        <span className="status">{matchStatusLabel}</span>
      </Fragment>
    )
  }

  /**
   * Case 2 — 결과 페이지, 종료된 경기 (isMatchupResult = true, isLiveMatch = false)
   * : 경기 날짜/시간 + 최종 스코어 + 배당
   */
  function renderResultGameInfo() {
    return (
      <Fragment>
        <div className="date-info-wrap">
          <span className="date">{dateOfMatchText}</span>
          <span className="time">{timeText}</span>
        </div>
        <div className="score">
          <span className={`home-score ${homeClassName}`}>{hScore}</span>
          <span className="center"/>
          <span className={`away-score ${awayClassName}`}>{aScore}</span>
        </div>
        {renderDividendBoard()}
      </Fragment>
    )
  }

  /**
   * Case 3 — 일반 프리매치 상세 (isLiveMatch = false, isMatchupResult = false)
   * : 경기 날짜/시간 + 배당
   */
  function renderPreMatchGameInfo() {
    return (
      <Fragment>
        <span className="date">{dateOfMatchText}</span>
        <span className="time">{timeText}</span>
        {renderDividendBoard()}
      </Fragment>
    )
  }

  const gameInfoCenterElem = isLiveMatch
    ? renderLiveGameInfo()
    : isMatchupResult
      ? renderResultGameInfo()
      : renderPreMatchGameInfo()

  // ─── GamePreview (팀 정보 + 중앙 스코어/배당) ───────────────────────────────
  const eleGamePreview = (
    <GamePreviewWrap id="gamePreviewWrap" className="game-preview-wrap">
      <MatchWrapper>
        <div className="match-block-header match-detail">
          <div className={gameKey === GAME_TYPE.LIVE ? 'tournament-info live' : 'tournament-info'}>
            <div className="tournament-wrap">
              {isLsportsEnv() ? LS_SPORT_ID_ICON_MAP[sportId] : SPORT_ID_ICON_MAP[sportId]}
              <span className="category-name">{categoryName}</span>
              <img src={tournamentEmblem || LeagueEmblemImg} alt="토너먼트 엠블럼" className="tournament-emblem"/>
              <span className="tournament-name" css={TxtLimit}>
                {tournamentName}
              </span>
            </div>
          </div>
        </div>
      </MatchWrapper>

      <MatchTeamInfoWrap className={detailBetClassName}>
        {/* 라이브일 경우 종목별 배경 이미지 */}
        {gameKey === GAME_TYPE.LIVE && (
          <div className="live-detail-bg">
            <img src={matchGroupBgImage} alt="종목 라이브 배경" className="live-detail-bg--img"/>
          </div>
        )}

        <div className="game-info-team home">
          <img src={HomeTagImg} alt="홈팀" className={`${homeTagClass} ${homeTagBaseballClass}`}/>
          <img
            src={homeTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]}
            alt="home team emblem"
            className="emblem"
          />
          <span className="team-name">{homeTeamName}</span>
        </div>

        <div className="game-info-center">
          {gameInfoCenterElem}
        </div>

        <div className="game-info-team away">
          <img
            src={awayTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]}
            alt="away team emblem"
            className="emblem"
          />
          <span className="team-name">{awayTeamName}</span>
        </div>

        {(winRatio > 0 || drawRatio > 0 || loseRatio > 0) && (
          <MatchChart className="game-info-match-chart">
            <div className="chart-info">
              {winRatio > 0 && <p className="chart-info-name win">승 {winRatio}%</p>}
              {drawRatio > 0 && <p className="chart-info-name draw">무 {drawRatio}%</p>}
              {loseRatio > 0 && <p className="chart-info-name lose">패 {loseRatio}%</p>}
            </div>
            <div className="chart-bar">
              {winRatio > 0 && <span className="chart-item-value win" style={{ flex: winRatio }}/>}
              {drawRatio > 0 && <span className="chart-item-value draw" style={{ flex: drawRatio }}/>}
              {loseRatio > 0 && <span className="chart-item-value lose" style={{ flex: loseRatio }}/>}
            </div>
          </MatchChart>
        )}

        {isShowMatchRecordButton(gameKey, pageType, sportId) && (
          <MatchRecordButton
            pageType={pageType}
            matchRecordSimpleMatchInfo={{
              matchId,
              sportId,
              homeScore: hScore,
              awayScore: aScore,
              homeTeamName,
              awayTeamName,
              homeTeamEmblem,
              awayTeamEmblem,
              matchStatusName: matchStatusLabel,
              isLiveMatch: gameKey === GAME_TYPE.LIVE,
            }}
          />
        )}
      </MatchTeamInfoWrap>
    </GamePreviewWrap>
  )

  // ─── 비주얼라이제이션 공지 ──────────────────────────────────────────────────
  const notice = isVisualSupportOn && visualSupport && (
    <VisualNotice onClick={() => DialogUtils.showLiveDetailNoticePopup()} className="cursor-pointer">
      <ToolTipIco/>위 정보는 실제 경기상황과 다를 수 있습니다.
    </VisualNotice>
  )

  /**
   * eleVisualSupport 출력 조건
   * - isVisualSupportOn && visualSupport  : 비주얼라이제이션 iframe 표시
   * - isVisualSupportOn && !visualSupport : GamePreview 표시 (해당 종목 iframe 미지원)
   * - !isVisualSupportOn                  : GamePreview 표시 (라이브/진행 중 아님)
   */
  const eleVisualSupport = isVisualSupportOn ? (
    <Fragment>
      {visualSupport ? (
        <Fragment>
          {/* 비주얼라이제이션 iframe */}
          <IframeWrap id="betIframeWrap" className={`${iframeWrapClassName} ${iframeWrapTypeClassName}`}>
            <img src={HomeTagImg} alt="홈팀" className={`${homeTagClass} ${homeTagBaseballClass}`}/>
            <Iframe url={convertedUrl} id="extendedIframe" scrolling="no" frameBorder={0}
                    className={gameKey === GAME_TYPE.LIVE && 'live-iframe'}/>
          </IframeWrap>
          {/* 라이브 - 스크롤 시 나타나는 스코어 영역 iframe */}
          {gameKey === GAME_TYPE.LIVE && (
            <LiveScoreIframeWrap id="betIframeScoreWrap"
                                 className={`${liveScoreIframeWrapClassName} ${iframeWrapTypeClassName} ${homeTagBaseballClass}`}>
              <img src={HomeTagImg} alt="홈팀" className={`${homeTagClass} ${homeTagBaseballClass}`}/>
              <Iframe url={convertedUrl} scrolling="no" frameBorder={0}/>
            </LiveScoreIframeWrap>
          )}
        </Fragment>
      ) : (
        eleGamePreview
      )}
    </Fragment>
  ) : (
    eleGamePreview
  )

  return (
    <Fragment>
      {eleVisualSupport}
      {notice}
      <MatchSimpleInfoWrap className={simpleMatchInfoClassName}>
        <span className="bg"/>
        <div className="simple-info">
          <img src={HomeTagImg} alt="홈팀" className="home-tag-img"/>
          <span className="simple-info--home-team">
            <img
              src={homeTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]}
              alt="home team emblem"
              className="emblem"
            />
            <span className="team-name">{homeTeamName}</span>
          </span>
          {/* 라이브 탭에서는 vs 텍스트 대신 스코어를 표시하므로 숨김 */}
          {gameKey !== GAME_TYPE.LIVE && <span className="simple-info--vs">vs</span>}
          {/* 라이브 또는 결과 페이지에서 스코어 표시 */}
          {(isLiveMatch || isMatchupResult) && (
            <span className="simple-info--score">
              <span className={`${homeScoreNumClassName} score-num home`}>{styleHomeScore}</span>
              <span className="score-center">:</span>
              <span className={`${awayScoreNumClassName} score-num away`}>{styleAwayScore}</span>
            </span>
          )}
          <span className="simple-info--away-team">
            <span className="team-name">{awayTeamName}</span>
            <img
              src={awayTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]}
              alt="away team emblem"
              className="emblem"
            />
          </span>
        </div>
      </MatchSimpleInfoWrap>
    </Fragment>
  )
})
